import { useState } from "react";
import { Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";

const COUNTRIES = [
  "Russia", "Georgia", "Kazakhstan", "Uzbekistan", "Kyrgyzstan",
  "Philippines", "Bangladesh", "Nepal", "Egypt",
  "UK", "Canada", "Australia", "New Zealand", "USA", "Greece", "Germany",
  "Other",
];

const initialForm = {
  name: "",
  phone: "",
  email: "",
  neet_score_type: "actual",
  neet_score: "",
  preferred_country: "Russia",
  parent_involvement: "",
  budget_band: "",
  notes: "",
  utm_source: "manual",
  utm_campaign: "",
};

export default function AddLeadDialog({ open, onOpenChange, onCreated, pipeline }) {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const setField = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((p) => ({ ...p, [k]: v }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Name and phone are required.");
      return;
    }
    // NEET score is optional now — only validate if provided
    let score = null;
    if (form.neet_score !== "" && form.neet_score !== null && form.neet_score !== undefined) {
      const parsed = Number(form.neet_score);
      if (Number.isNaN(parsed) || parsed < 0 || parsed > 720) {
        toast.error("NEET score must be 0-720.");
        return;
      }
      score = parsed;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        email: form.email.trim() || null,
        neet_score: score,
        parent_involvement: form.parent_involvement || null,
        budget_band: form.budget_band || null,
        utm_campaign: form.utm_campaign || null,
        notes: form.notes || null,
        auto_assign: true,
        pipeline: pipeline || undefined,
      };
      const r = await api.post("/admin/leads/manual", payload);
      toast.success(`Lead "${r.data.lead.name}" created.`);
      setForm(initialForm);
      onOpenChange(false);
      onCreated?.(r.data.lead);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-xl flex-col p-0 sm:w-full"
        data-testid="add-lead-dialog"
      >
        <DialogHeader className="shrink-0 border-b border-slate-100 px-6 pb-3 pt-5">
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-[#0A2540]" />{" "}
            {pipeline === "counseling"
              ? "Add counselling lead"
              : pipeline === "general"
                ? "Add lead"
                : "Add lead manually"}
          </DialogTitle>
          <DialogDescription>
            For walk-ins, phone enquiries, or referrals. Goes through scoring
            automatically.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={onSubmit}
          className="flex min-h-0 flex-1 flex-col"
        >
          <div className="grid flex-1 gap-4 overflow-y-auto px-6 py-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label htmlFor="al-name">Full name *</Label>
              <Input id="al-name" value={form.name} onChange={setField("name")}
                required minLength={2} data-testid="al-name" />
            </div>
            <div>
              <Label htmlFor="al-phone">Phone *</Label>
              <Input id="al-phone" value={form.phone} onChange={setField("phone")}
                required minLength={6} placeholder="9876543210" data-testid="al-phone" />
            </div>
            <div>
              <Label htmlFor="al-email">Email</Label>
              <Input id="al-email" type="email" value={form.email} onChange={setField("email")}
                placeholder="optional" data-testid="al-email" />
            </div>
            <div>
              <Label>NEET score type</Label>
              <Select value={form.neet_score_type} onValueChange={setField("neet_score_type")}>
                <SelectTrigger data-testid="al-score-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="actual">Actual</SelectItem>
                  <SelectItem value="expected">Expected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="al-score">NEET score (0-720)</Label>
              <Input id="al-score" type="number" min="0" max="720"
                value={form.neet_score} onChange={setField("neet_score")}
                placeholder="optional"
                data-testid="al-score" />
            </div>
            <div className="sm:col-span-2">
              <Label>Preferred country</Label>
              <Select value={form.preferred_country} onValueChange={setField("preferred_country")}>
                <SelectTrigger data-testid="al-country"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Parent involvement</Label>
              <Select value={form.parent_involvement || "none"} onValueChange={(v) => setField("parent_involvement")(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="al-parent"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Not set —</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Budget band</Label>
              <Select value={form.budget_band || "none"} onValueChange={(v) => setField("budget_band")(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="al-budget"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Not set —</SelectItem>
                  <SelectItem value="under_5l">Under ₹5L</SelectItem>
                  <SelectItem value="5_10l">₹5L – ₹10L</SelectItem>
                  <SelectItem value="10_20l">₹10L – ₹20L</SelectItem>
                  <SelectItem value="above_20l">Above ₹20L</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="al-notes">Initial notes</Label>
              <Textarea id="al-notes" value={form.notes} onChange={setField("notes")}
                rows={2} placeholder="Walk-in, parent called, referred by…"
                data-testid="al-notes" />
            </div>
          </div>
          <DialogFooter className="shrink-0 gap-2 border-t border-slate-100 bg-white px-6 py-3 sm:gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}
              data-testid="al-cancel">Cancel</Button>
            <Button type="submit" disabled={submitting}
              className="bg-[#0A2540] text-white hover:bg-[#1E3A5F]" data-testid="al-submit">
              {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating…</> : "Create lead"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
